import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Calendar
} from 'lucide-react';
import {
  getJourneyMilestones,
  addJourneyMilestone,
  updateJourneyMilestone,
  deleteJourneyMilestone
} from '../../utils/contentManager';

const JourneyManagement = () => {
  const [milestones, setMilestones] = useState([]);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ year: '', event: '' });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadMilestones();
  }, []);

  const loadMilestones = () => {
    const journeyData = getJourneyMilestones();
    const sortedData = journeyData.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    setMilestones(sortedData);
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingMilestone(null);
    setFormData({
      year: new Date().getFullYear().toString(),
      event: ''
    });
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone.id);
    setShowAddForm(true);
    setFormData({
      year: milestone.year,
      event: milestone.event
    });
  };

  const handleSave = () => {
    try {
      if (!formData.year.trim() || !formData.event.trim()) {
        setError('Year and event are required');
        return;
      }

      const year = parseInt(formData.year);
      if (isNaN(year) || year < 1900 || year > 2100) {
        setError('Please enter a valid year');
        return;
      }

      const milestoneData = { ...formData, year: year.toString() };

      if (editingMilestone) {
        const result = updateJourneyMilestone(editingMilestone, milestoneData);
        result ? setSuccess('Journey milestone updated successfully') : setError('Failed to update milestone');
      } else {
        const result = addJourneyMilestone(milestoneData);
        result ? setSuccess('Journey milestone added successfully') : setError('Failed to add milestone');
      }

      loadMilestones();
      handleCancel();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save milestone');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this milestone?')) {
      try {
        const result = deleteJourneyMilestone(id);
        if (result) {
          setSuccess('Journey milestone deleted successfully');
          loadMilestones();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete milestone');
          setTimeout(() => setError(''), 3000);
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete milestone');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingMilestone(null);
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Journey Management</h1>
          <p className="text-gray-600 mt-2">Manage your company journey milestones</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Milestone</span>
        </motion.button>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year *
                </label>
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="2024"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Description *
                </label>
                <textarea
                  value={formData.event}
                  onChange={(e) => handleInputChange('event', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe what happened in this year..."
                />
              </div>
            </div>

            <div className="flex items-center space-x-4 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700
                transition-colors duration-200 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{editingMilestone ? 'Update' : 'Save'}</span>
              </motion.button>
              <button
                onClick={handleCancel}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400
                transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Timeline View */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Company Journey Timeline</h3>
        {milestones.length > 0 ? (
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative flex items-start space-x-6"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center
                  text-white font-bold relative z-10">
                    {milestone.year}
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-lg p-4 relative">
                    <div className="absolute top-6 left-0 w-0 h-0 border-t-8 border-b-8 border-r-8
                    border-transparent border-r-gray-50 transform -translate-x-2"></div>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span className="text-lg font-semibold text-blue-600">
                            {milestone.year}
                          </span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{milestone.event}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(milestone)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(milestone.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Calendar className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-gray-500 mb-4">No milestones found</p>
            <button
              onClick={handleAdd}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first milestone
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JourneyManagement;
